"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Snowflake, ShieldCheck, Utensils, PartyPopper, ChefHat, Store, Truck, Warehouse, Sparkles, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const industryApplications = [{
  id: "catering",
  title: "Catering",
  description: "Potential on-site refrigerated or freezer storage for ingredients, prepared food, and beverages during catering operations.",
  badge: "Catering",
  iconName: "catering",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=kwq3v1o9ffb",
  alt: "Refrigerated trailer supporting catering operations."
}, {
  id: "events",
  title: "Events & Special Events",
  description: "Potential mobile cold storage for food and beverages at indoor and outdoor events, private functions, and celebrations. Trailer size and suitability are confirmed individually.",
  badge: "Events",
  iconName: "events",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-12-2026-06_46_54-PM-1-1.png?v=hyc7kkqapdi",
  alt: "Refrigerated trailer stationed at an outdoor event."
}, {
  id: "food-service",
  title: "Food Service",
  description: "Potential additional refrigerated or freezer capacity for restaurants, commercial kitchens, and food-service operations.",
  badge: "Food Service",
  iconName: "food-service",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png?v=p5po664zgxo",
  alt: "Cold storage trailer for commercial food service."
}, {
  id: "farmers-markets",
  title: "Farmers Markets",
  description: "Potential mobile refrigeration for food and beverage inventory during farmers-market operations.",
  badge: "Farmers Markets",
  iconName: "farmers-markets",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=axowytib9",
  alt: "Mobile cold storage unit for farmers markets and fresh produce."
}, {
  id: "mobile-vendors",
  title: "Mobile Vendors",
  description: "Potential refrigerated or freezer storage for mobile food and beverage vendors.",
  badge: "Mobile Vendors",
  iconName: "mobile-vendors",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-12-2026-06_46_23-PM-1-1.png?v=b5jlzo1acuq",
  alt: "Refrigerated trailer supporting mobile vendors and concessions."
}, {
  id: "temporary-storage",
  title: "Temporary Storage",
  description: "Potential additional cold-storage capacity when temporary refrigerated or freezer space is needed.",
  badge: "Temporary Storage",
  iconName: "temporary-storage",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png?v=yfdkn8ljahm",
  alt: "Mobile trailer providing temporary on-site cold storage."
}];
export default function Industries() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (type: string) => {
    switch (type) {
      case "catering":
        return <Utensils className="size-5 text-primary" />;
      case "events":
        return <PartyPopper className="size-5 text-primary" />;
      case "food-service":
        return <ChefHat className="size-5 text-primary" />;
      case "farmers-markets":
        return <Store className="size-5 text-primary" />;
      case "mobile-vendors":
        return <Truck className="size-5 text-primary" />;
      case "temporary-storage":
        return <Warehouse className="size-5 text-primary" />;
      case "special-events":
        return <Sparkles className="size-5 text-primary" />;
      default:
        return <Snowflake className="size-5 text-primary" />;
    }
  };
  return <section id="industries-we-serve" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-foreground mb-4">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            VERSATILE COLD STORAGE
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Who We Serve
          </h1>
          
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Fridge Fleet offers mobile refrigerated and freezer trailer options for catering, events, food service, farmers markets, mobile vendors, grocery operations, food distribution, and temporary commercial storage. Trailer size, availability, and suitability are confirmed for each rental.
          </p>
        </div>

        {/* Responsive Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {industryApplications.map((app, i) => <motion.div key={app.id} variants={itemVariants} data-index={i} className="flex">
              <Card className="flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200 rounded-lg w-full">
                <div>
                  {/* Photo Box */}
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted flex items-center justify-center">
                    <img className="w-full h-full object-cover" src={app.image} alt={app.alt} loading="lazy" />
                    <div className="absolute top-3 left-3 max-w-[calc(100%-1.5rem)] truncate bg-background/90 backdrop-blur-xs border border-border/80 px-2.5 py-1 rounded-md text-xs font-medium text-foreground">
                      {app.badge}
                    </div>
                  </div>

                  {/* Card Content */}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted border border-border">
                        {renderIcon(app.iconName)}
                      </div>
                      <h2 className="text-xl font-bold text-card-foreground font-default tracking-tight">
                        {app.title}
                      </h2>
                    </div>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {app.description}
                    </p>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-0">
                  <div className="border-t border-border pt-4 flex items-center justify-between gap-2">
                    <Link to="/RefrigeratedTrailersPage" className="text-xs font-medium text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors min-w-0">
                      <ShieldCheck className="size-4 text-accent-foreground shrink-0" />
                      <span className="truncate">View Trailer Options</span>
                    </Link>
                    <Link to="/ContactRequestAQuotePage" className="text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1 transition-colors shrink-0">
                      Get Quote <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <p className="mt-6 text-sm text-muted-foreground text-center">
          Trailer size, availability, and suitability for each application are confirmed individually.
        </p>

        {/* Bottom Dispatch / Action Banner */}
        <div className="mt-14 md:mt-18 rounded-xl border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
          <div className="space-y-1.5 max-w-2xl">
            <h3 className="text-lg md:text-xl font-bold text-card-foreground font-default">
              Need temperature-controlled capacity?
            </h3>
            <p className="text-sm text-muted-foreground">
              Serving Southern California and our listed Las Vegas-area communities.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/ContactRequestAQuotePage">
                Request a Quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            
            <a href="tel:2135218821" className="inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium text-foreground hover:text-primary border border-border rounded-md bg-muted/40 hover:bg-muted/80 transition-colors w-full sm:w-auto">
              <Phone className="size-4 text-primary" />
              213-521-8821
            </a>
          </div>
        </div>
      </div>
    </section>;
}